import { createReducer } from "reduxsauce"
import { get, omit } from "lodash"
import { takeLatest, select, put } from "redux-saga/effects"
import actionCreator from "helpers/actionCreator"
import { Types as LoaderTypes } from "model/data"
import { Types as BaseTypes } from "modules/portfolio-root/model"
import { Types as HistoryTypes } from "../../../actions/app/history"
import { Types as DefaultUnitTypes } from "modules/default-metric-units/model"
import { getCategoryItemsSubset } from "./selectors/data"
import { AGGREGATION_FUNCTIONS } from "helpers/factorClassification"
import { getDefaultMetricUnits } from "modules/default-metric-units/selectors"

export const { Types, Creators } = actionCreator("ompnt/modules/analyzeHistoricalExposure", {
  toggle: {
    props: ["id", "currentState"],
    track: true,
    trackType: "analyze-exposure-graph-legend-factor",
    trackPayload: ({ id, currentState }) => ({ factor: id, state: currentState ? "on" : "off" }),
  },
  focusChartLine: ["focusedChartLine"],
  blurChartLine: [],
  setDisplayType: {
    props: ["displayType"],
    track: true,
    trackType: "analyze-exposure-category-selection",
    trackPayload: ({ displayType }) => ({ type: displayType }),
  },
  toggleLitmusView: [],
  trackCSVDownload: {
    track: true,
    trackType: "analyze-exposure-graph-timeseries-download",
  },
  toggleOnly: {
    props: ["id", "contentSetId"],
    track: true,
    trackType: "analyze-exposure-graph-legend-factor",
    trackPayload: ({ id, contentSetId }) => ({ factor: id, contentSetId }),
  },
  toggleOnlySuccess: ["selected", "only", "items"],
  setExposureUnit: ["unit"],
})

const DEFAULT_STATE = {
  displayType: "style",
  focusedChartLine: null,
  contributorsDetailsVisible: false,
  selectedContributor: null,
  disabled: {},
  litmusViewVisible: false,
  only: null,
  styleAggregation: AGGREGATION_FUNCTIONS.MAX,
  unit: null,
}

export const handleNavigate = (state, action) => {
  const litmus = get(action, "location.query.daily", null)

  if (litmus) {
    return { ...state, litmusViewVisible: true }
  } else {
    return state
  }
}

function handleSetDisplayType(state, { displayType }) {
  return { ...state, displayType, only: null }
}

function handleToggle(state, { id }) {
  let disabled
  let focusedChartLine

  if (state.disabled[id]) {
    focusedChartLine = id
    disabled = omit(state.disabled, id)
  } else {
    focusedChartLine = null
    disabled = { [id]: true, ...state.disabled }
  }
  return { ...state, disabled, focusedChartLine, only: null }
}

function handleFocusChartLine(state, { focusedChartLine }) {
  return { ...state, focusedChartLine }
}

function handleBlurChartLine(state) {
  return { ...state, focusedChartLine: null }
}

function handleToggleLitmusView(state) {
  return { ...state, litmusViewVisible: !state.litmusViewVisible }
}

function handleToggleOnlySuccess(state, { selected, only, items }) {
  const disabled = {}
  let focusedChartLine = null
  let onlyDisplay = null

  if (only) {
    focusedChartLine = selected
    onlyDisplay = selected
    items.forEach(item => {
      if (item.id !== selected) {
        disabled[item.id] = true
      }
    })
  }

  return { ...state, disabled, focusedChartLine, only: onlyDisplay }
}

function handleSetModel(state) {
  return { ...state, only: null, focusedChartLine: null }
}

function handleLoadSuccess(state, action) {
  if (action.resource === "portfolio") {
    // Navigate occurs before the load success and we're checking for 'daily' query string param to display the litmus
    return { ...DEFAULT_STATE, litmusViewVisible: state.litmusViewVisible }
  }
  return state
}

function handleSetExposureUnit(state, { unit }) {
  return { ...state, unit }
}

export default createReducer(DEFAULT_STATE, {
  [HistoryTypes.NAVIGATE]: handleNavigate,
  [Types.TOGGLE]: handleToggle,
  [Types.FOCUS_CHART_LINE]: handleFocusChartLine,
  [Types.BLUR_CHART_LINE]: handleBlurChartLine,
  [Types.SET_DISPLAY_TYPE]: handleSetDisplayType,
  [Types.TOGGLE_LITMUS_VIEW]: handleToggleLitmusView,
  [Types.TOGGLE_ONLY_SUCCESS]: handleToggleOnlySuccess,
  [BaseTypes.SET_MODEL]: handleSetModel,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [Types.SET_EXPOSURE_UNIT]: handleSetExposureUnit,
})

function* handleToggleOnly({ id, contentSetId }) {
  const { items, only } = yield select(state => ({
    items: getCategoryItemsSubset(state, { contentSetId, isStyleFactorsAdditive: false }),
    only: state.modules.analyzeHistoricalExposure.only,
  }))

  yield put(Creators.toggleOnlySuccess(id, only !== id, items))
}

function* handleUpdateDefaultMetrics() {
  const { exposure: exposureUnit } = yield select(getDefaultMetricUnits)
  yield put(Creators.setExposureUnit(exposureUnit))
}

export function* saga() {
  yield takeLatest(Types.TOGGLE_ONLY, handleToggleOnly)
  yield takeLatest(DefaultUnitTypes.UPDATE_DEFAULT_METRICS, handleUpdateDefaultMetrics)
}
